import httpClient from '../lib/httpClient';
import { 
  OutsourcedShow, 
  OutsourcedShowDTO, 
  OutsourcedShowFilters,
  TransferShowAdditionalFields,
  PaginatedOutsourcedShowsResponse
} from '../types/OutsourcedShow';

// Use exact API structure from studio project
const apiPrefix = '/api';
const studioApiPrefix = '/studio';
const BASE_URL = `${apiPrefix}${studioApiPrefix}/outsourced-shows`;

export const outsourcedShowService = {
  // Create Outsourced Show
  async createOutsourcedShow(outsourcedShowDTO: OutsourcedShowDTO): Promise<OutsourcedShow> {
    try {
      return await httpClient.post(BASE_URL, outsourcedShowDTO);
    } catch (error) {
      console.error('Unable to create outsourced show');
      throw error;
    }
  },

  // Get All Shows (with filters and pagination)
  async getOutsourcedShows(filters?: OutsourcedShowFilters): Promise<PaginatedOutsourcedShowsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.offset !== undefined) params.append('offset', filters.offset.toString());
      if (filters?.limit !== undefined) params.append('limit', filters.limit.toString());
      if (filters?.source) params.append('source', filters.source);
      if (filters?.isSelected !== undefined) params.append('isSelected', filters.isSelected.toString());
      if (filters?.genre) params.append('genre', filters.genre);
      if (filters?.originCountry) params.append('originCountry', filters.originCountry);
      if (filters?.tierAsPerPartner) params.append('tierAsPerPartner', filters.tierAsPerPartner);
      if (filters?.tierAsPerBenchmarking) params.append('tierAsPerBenchmarking', filters.tierAsPerBenchmarking);
      
      const queryString = params.toString();
      const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
      return await httpClient.get(url);
    } catch (error) {
      console.error('Unable to fetch outsourced shows');
      throw error;
    }
  },

  // Get Outsourced Shows by Main Show ID
  async getOutsourcedShowsByMainShowId(mainShowId: string): Promise<OutsourcedShow[]> {
    try {
      const url = `${BASE_URL}?mainShowId=${mainShowId}`;
      return await httpClient.get(url);
    } catch (error) {
      console.error(`Unable to fetch outsourced shows for main show ${mainShowId}`);
      throw error;
    }
  },

  // Search Shows
  async searchOutsourcedShows(searchTerm: string): Promise<OutsourcedShow[]> {
    try {
      return await httpClient.get(`${BASE_URL}/search?q=${encodeURIComponent(searchTerm)}`);
    } catch (error) {
      console.error('Unable to search outsourced shows');
      throw error;
    }
  },

  // Get Show by ID
  async getOutsourcedShowById(id: string): Promise<OutsourcedShow> {
    try {
      return await httpClient.get(`${BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Unable to fetch outsourced show ${id}`);
      throw error;
    }
  },

  // Update Show
  async updateOutsourcedShow(id: string, updateData: Partial<OutsourcedShowDTO>): Promise<OutsourcedShow> {
    try {
      console.log('Updating outsourced show:', id, 'with data:', updateData);
      console.log('Update URL:', `${BASE_URL}/${id}`);
      
      // Include the id (showId) in the request body as required by the API
      const requestBody = {
        ...updateData,
        id: updateData.showId || id
      };
      
      console.log('Request body with id:', requestBody);
      const result = await httpClient.put(`${BASE_URL}/${id}`, requestBody);
      console.log('Update result:', result);
      return result;
    } catch (error) {
      console.error(`Unable to update outsourced show ${id}:`, error);
      throw error;
    }
  },

  // Delete Show
  async deleteOutsourcedShow(id: string): Promise<void> {
    try {
      await httpClient.delete(`${BASE_URL}/${id}`, {
        resourceId: id
      });
    } catch (error) {
      console.error(`Unable to delete outsourced show ${id}`);
      throw error;
    }
  },

  // Transfer to Main Show
  async transferOutsourcedShowToMain(id: string, additionalFields?: TransferShowAdditionalFields): Promise<any> {
    try {
      console.log('=== TRANSFER REQUEST DEBUG ===');
      console.log('ID:', id);
      console.log('Additional Fields:', additionalFields);
      console.log('URL:', `${BASE_URL}/${id}/transfer-to-show`);
      console.log('Request Body:', JSON.stringify(additionalFields || {}, null, 2));
      console.log('=== END DEBUG ===');
      
      return await httpClient.post(`${BASE_URL}/${id}/transfer-to-show`, additionalFields || {});
    } catch (error) {
      console.error(`Unable to transfer outsourced show ${id} to main show`);
      throw error;
    }
  }
};